import { useEffect, useState, useCallback } from 'react'

const STORAGE_KEY = 'redevicer:compare-listings'
const MAX_COMPARE = 4
const EVENT = 'redevicer:compare-updated'

function readIds(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr.filter((n) => typeof n === 'number') : []
  } catch {
    return []
  }
}

function writeIds(ids: number[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
    window.dispatchEvent(new Event(EVENT))
  } catch {
    /* noop */
  }
}

export function useCompare() {
  const [ids, setIds] = useState<number[]>(() => readIds())

  useEffect(() => {
    function onUpdate() { setIds(readIds()) }
    window.addEventListener(EVENT, onUpdate)
    window.addEventListener('storage', onUpdate)
    return () => {
      window.removeEventListener(EVENT, onUpdate)
      window.removeEventListener('storage', onUpdate)
    }
  }, [])

  const has = useCallback((id: number) => ids.includes(id), [ids])

  const toggle = useCallback((id: number): 'added' | 'removed' | 'full' => {
    const current = readIds()
    if (current.includes(id)) {
      writeIds(current.filter((x) => x !== id))
      return 'removed'
    }
    if (current.length >= MAX_COMPARE) return 'full'
    writeIds([...current, id])
    return 'added'
  }, [])

  const clear = useCallback(() => writeIds([]), [])

  return { ids, count: ids.length, has, toggle, clear, max: MAX_COMPARE }
}
